import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { errorMessage } from '../../services/apiError';
import { profileService } from '../../services/profileService';
import { useToast } from '../Toast/Toast';

import './BYOKSettingsModal.css';

interface BYOKKey { index: number; label: string; masked_token: string; created_at: string; }

interface BYOKSettingsModalProps { isOpen: boolean; onClose: () => void; onKeysChange?: () => void; }

const HowToTab: React.FC = () => (
  <div className="byok-instructions">
    <h3>How to get your Claude OAuth Token</h3>
    <div className="byok-step">
      <div className="byok-step-label">Step 1: Install Claude Code CLI</div>
      <div className="byok-code-block">npm install -g @anthropic-ai/claude-code</div>
    </div>
    <div className="byok-step">
      <div className="byok-step-label">Step 2: Run setup-token</div>
      <div className="byok-code-block">claude setup-token</div>
    </div>
    <div className="byok-step">
      <div className="byok-step-label">Step 3: Copy the token</div>
      <p>Copy the OAuth token from the CLI output and paste it in the &quot;My Keys&quot; tab.</p>
      <div className="byok-code-block">
        <span style={{ color: '#a0e0a0' }}>{'✓ Long-lived authentication token created successfully!'}</span>
        {'\n\n'}
        {'Your OAuth token (valid for 1 year):\n\n'}
        <span style={{ color: '#e8a838' }}>{'sk-ant-oat01-xxxxxxxxxxxx...'}</span>
        {'\n\n'}
        {'Store this token securely. You won\'t be able to see it again.'}
      </div>
    </div>
  </div>
);

const KeyIcon: React.FC = () => (
  <svg className="byok-key-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="byok-trash-icon">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const KeyItem: React.FC<{ k: BYOKKey; onDelete: (i: number) => void }> = ({ k, onDelete }) => (
  <div className="byok-key-item">
    <div className="byok-key-left">
      <KeyIcon />
      <div className="byok-key-info">
        <h4>{k.label}</h4>
        <code>{k.masked_token}</code>
      </div>
    </div>
    <button className="byok-key-delete" onClick={() => onDelete(k.index)} type="button" title="Delete key">
      <TrashIcon />
    </button>
  </div>
);

interface AddKeyFormProps { onAdd: (l: string, t: string) => Promise<boolean>; saving: boolean }

interface TokenInputProps {
  value: string;
  onChange: (val: string) => void;
}

const TokenInput: React.FC<TokenInputProps> = ({ value, onChange }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="byok-token-wrapper">
      <input
        type="text"
        name="byok_token"
        autoComplete="off"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        className={show ? '' : 'byok-token-masked'}
        placeholder="sk-ant-oat01-..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="byok-toggle-vis"
        onClick={() => setShow((v) => !v)}
        title={show ? 'Hide token' : 'Show token'}
      >
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  );
};

const AddKeyForm: React.FC<AddKeyFormProps> = ({ onAdd, saving }) => {
  const [label, setLabel] = useState('');
  const [token, setToken] = useState('');
  const handleSubmit = async () => {
    const ok = await onAdd(label || 'My Key', token);

    if (ok) { setLabel(''); setToken(''); }
  };

  return (
    <form className="byok-add-form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <div className="byok-form-title">Add New Key</div>
      <div className="byok-field">
        <label className="byok-field-label">Label</label>
        <input
          type="text"
          name="byok_label"
          autoComplete="off"
          placeholder="e.g. Personal Key"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <div className="byok-field">
        <label className="byok-field-label">OAuth Token</label>
        <TokenInput value={token} onChange={setToken} />
      </div>
      <button className="byok-save-btn" onClick={handleSubmit} disabled={saving || !token.trim()} type="button">
        {saving ? 'Saving…' : '+ Add Key'}
      </button>
    </form>
  );
};

const ErrorWithLink: React.FC<{ error: string; onLinkClick?: () => void }> = ({ error, onLinkClick }) => {
  if (!error) return null;

  const parts = error.split("'How to Generate'");

  if (parts.length < 2 || !onLinkClick) return <div className="byok-error">{error}</div>;

  return (
    <div className="byok-error">
      {parts[0]}
      <button type="button" className="byok-error-link" onClick={onLinkClick}>How to Generate</button>
      {parts[1]}
    </div>
  );
};

const KeysTab: React.FC<{
  keys: BYOKKey[]; error: string; saving: boolean;
  onAdd: (l: string, t: string) => Promise<boolean>; onDelete: (i: number) => void;
  onSwitchToHowto?: () => void;
}> = ({ keys, error, saving, onAdd, onDelete, onSwitchToHowto }) => (
  <>
    <ErrorWithLink error={error} onLinkClick={onSwitchToHowto} />
    <div className="byok-keys-list">
      {keys.length === 0 ? (
        <div className="byok-no-keys">
          <KeyIcon />
          <p>No keys saved yet</p>
          <span>Add your Claude OAuth token below to get started.</span>
        </div>
      ) : keys.map((k) => <KeyItem key={k.index} k={k} onDelete={onDelete} />)}
    </div>
    <AddKeyForm onAdd={onAdd} saving={saving} />
  </>
);

const ModalTabs: React.FC<{ tab: string; setTab: (t: 'keys' | 'howto') => void }> = ({ tab, setTab }) => (
  <div className="byok-tabs">
    <button className={`byok-tab ${tab === 'keys' ? 'active' : ''}`} onClick={() => setTab('keys')} type="button">My Keys</button>
    <button className={`byok-tab ${tab === 'howto' ? 'active' : ''}`} onClick={() => setTab('howto')} type="button">How to Generate</button>
  </div>
);

type SetState = React.Dispatch<React.SetStateAction<{ keys: BYOKKey[]; error: string; saving: boolean }>>;

const submitKey = async (
  label: string, token: string,
  loadKeys: () => Promise<void>,
  onKeysChange?: () => void,
  toast?: { success: (msg: string) => void },
) => {
  await profileService.addBYOKKey(label, token);
  await loadKeys(); onKeysChange?.(); toast?.success('Token validated ✓');
};

const useKeyAdd = (setState: SetState, loadKeys: () => Promise<void>, onKeysChange?: () => void) => {
  const toast = useToast();

  return useCallback(async (label: string, token: string): Promise<boolean> => {
    setState((s) => ({ ...s, saving: true, error: '' }));
    try {
      await submitKey(label, token, loadKeys, onKeysChange, toast);

      return true;
    } catch (e: unknown) {
      setState((s) => ({
        ...s,
        error: errorMessage(e, 'We couldn’t save this key. Verify the token and try again.'),
      }));

      return false;
    } finally { setState((s) => ({ ...s, saving: false })); }
  }, [setState, loadKeys, onKeysChange, toast]);
};

const useKeyManager = (isOpen: boolean, onKeysChange?: () => void) => {
  const [state, setState] = useState<{ keys: BYOKKey[]; error: string; saving: boolean }>(
    { keys: [], error: '', saving: false },
  );
  const loadKeys = useCallback(async () => {
    try { const d = await profileService.getBYOKKeys();

      setState((s) => ({ ...s, keys: d.keys || [] })); }
    catch (error) {
      setState((state) => ({
        ...state,
        error: errorMessage(error, 'We couldn’t load your keys. Refresh and try again.'),
      }));
    }
  }, []);

  useEffect(() => { if (isOpen) loadKeys(); }, [isOpen, loadKeys]);

  const handleAdd = useKeyAdd(setState, loadKeys, onKeysChange);
  const handleDelete = async (index: number) => {
    try { await profileService.deleteBYOKKey(index); await loadKeys(); onKeysChange?.(); }
    catch (error) {
      setState((s) => ({
        ...s,
        error: errorMessage(error, 'We couldn’t delete this key. Refresh and try again.'),
      }));
    }
  };

  return { ...state, handleAdd, handleDelete };
};

const UpgradePrompt: React.FC<{ onUpgrade: () => void }> = ({ onUpgrade }) => (
  <div className="byok-upgrade-prompt">
    <div className="byok-upgrade-icon">🔒</div>
    <h3>BYOK is available on paid plans</h3>
    <p>Upgrade to a paid plan to bring your own API keys and unlock unlimited AI access.</p>
    <button
      className="byok-upgrade-btn app-action-btn app-action-btn--upgrade"
      onClick={onUpgrade}
      type="button"
    >
      Upgrade Now
    </button>
  </div>
);

export const BYOKSettingsModal: React.FC<BYOKSettingsModalProps> = ({ isOpen, onClose, onKeysChange }) => {
  const [tab, setTab] = useState<'keys' | 'howto'>('keys');
  const { keys, error, saving, handleAdd, handleDelete } = useKeyManager(isOpen, onKeysChange);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFreeTier = !user?.subscription_tier || user.subscription_tier === 'free';

  if (!isOpen) return null;

  return (
    <div className="byok-modal-overlay" onClick={onClose} role="presentation">
      <div className="byok-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="byok-modal-header">
          <h2>⚙️ BYOK Settings</h2>
          <button className="byok-close-btn" onClick={onClose} type="button">✕</button>
        </div>
        {isFreeTier ? (
          <div className="byok-content">
            <UpgradePrompt onUpgrade={() => { onClose(); navigate('/pricing'); }} />
          </div>
        ) : (
          <>
            <ModalTabs tab={tab} setTab={setTab} />
            <div className="byok-content">
              {tab === 'keys'
                ? (
                  <KeysTab
                    keys={keys} error={error} saving={saving}
                    onAdd={handleAdd} onDelete={handleDelete}
                    onSwitchToHowto={() => setTab('howto')}
                  />
                )
                : <HowToTab />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
